import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { Euro, CalendarDays, Users as UsersIcon, Receipt, TrendingUp, TrendingDown, Scissors, Clock } from 'lucide-react';

import Modal from '../ui/Modal';
import AnalyticsDateSelector from '@/components/analytics/AnalyticsDateSelector';
import { useEvents } from '@/contexts/EventsContext';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { useCompensations } from '@/contexts/CompensationsContext';
import { monthlyCostFromCompensation } from '@/services/employees/employeeCompensationService';

const euro = (n: number) => `€ ${Math.round(n).toLocaleString('it-IT')}`;
const PERIOD_DAYS: Record<string, number> = { day: 1, week: 7, month: 30.42, year: 365 };
const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const PRIMARY = '#D96C4F';

export interface StaffPerfTarget {
    id: string;
    name: string;
    initials: string;
    avatarColor?: string;
}

interface Props {
    hairdresser: StaffPerfTarget | null;
    isOpen: boolean;
    onClose: () => void;
}

const StaffPerformanceDetailModal: React.FC<Props> = ({ hairdresser, isOpen, onClose }) => {
    const { bookings, getPerformanceById } = useEvents();
    const { getValidBookings, calculateRevenue, filterDataByPeriod } = useAnalytics();
    const { getByHairdresserId } = useCompensations();

    const [period, setPeriod] = useState('month');
    // Intervallo personalizzato "da/a" (oltre ai preset). Attivo quando entrambe le date sono valide.
    const [customOpen, setCustomOpen] = useState(false);
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const customActive = customOpen && !!customFrom && !!customTo && customFrom <= customTo;

    const hairdresserId = hairdresser?.id;
    // Compenso dal context condiviso (caricato all'apertura della pagina Dipendenti).
    const comp = hairdresserId ? getByHairdresserId(hairdresserId) : null;

    const data = useMemo(() => {
        if (!hairdresserId) return null;
        const valid = getValidBookings(bookings || []);
        const own = (customActive
            ? valid.filter((b: any) => b.date >= customFrom && b.date <= customTo)
            : filterDataByPeriod(valid, period, 'bookings')
        ).filter((b: any) => b.hairdresser_id === hairdresserId);

        const revenue = calculateRevenue(own);
        const clients = new Set(own.map((b: any) => b.client_id).filter(Boolean)).size;
        const ticket = own.length > 0 ? revenue / own.length : 0;

        // Servizi effettuati (conteggio + fatturato)
        const svcMap = new Map<string, { count: number; revenue: number }>();
        for (const b of own) {
            const perf = getPerformanceById(b.performance_id);
            const name = perf?.name || 'Altro';
            const price = (perf?.price || 0) + (Number(b.price_adjustment) || 0);
            const cur = svcMap.get(name) || { count: 0, revenue: 0 };
            cur.count += 1;
            cur.revenue += price;
            svcMap.set(name, cur);
        }
        const services = Array.from(svcMap.entries())
            .map(([name, v]) => ({ name, ...v }))
            .sort((a, b) => b.count - a.count);

        // Giorni di maggiore attività (per giorno della settimana)
        const byWeekday = [1, 2, 3, 4, 5, 6, 0].map((dow) => ({
            name: WEEKDAYS[dow],
            value: own.filter((b: any) => new Date(b.date).getDay() === dow).length,
        }));

        // Distribuzione oraria
        const hourMap = new Map<number, number>();
        for (const b of own) {
            const h = parseInt(String(b.time).slice(0, 2), 10);
            if (!isNaN(h)) hourMap.set(h, (hourMap.get(h) || 0) + 1);
        }
        const byHour = Array.from(hourMap.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([h, v]) => ({ name: `${String(h).padStart(2, '0')}`, value: v }));

        return { own, revenue, clients, ticket, services, byWeekday, byHour };
    }, [bookings, period, customActive, customFrom, customTo, hairdresserId, getValidBookings, calculateRevenue, filterDataByPeriod, getPerformanceById]);

    // Costo del periodo (ratei inclusi)
    const monthlyCost = comp ? monthlyCostFromCompensation(comp) : 0;
    const customDays = customActive ? Math.max(1, (new Date(customTo).getTime() - new Date(customFrom).getTime()) / 86400000 + 1) : null;
    const periodDays = customDays ?? (PERIOD_DAYS[period] ?? (period === 'ever' ? 180 : 30.42));
    const costForPeriod = monthlyCost * (periodDays / 30.42);
    const margin = (data?.revenue || 0) - costForPeriod;
    const ratio = costForPeriod > 0 ? (data?.revenue || 0) / costForPeriod : null;
    const over = ratio != null && ratio >= 1;

    if (!hairdresser) return null;

    const kpis = [
        { icon: Euro, label: 'Produzione', value: euro(data?.revenue || 0) },
        { icon: CalendarDays, label: 'Appuntamenti', value: String(data?.own.length || 0) },
        { icon: UsersIcon, label: 'Clienti serviti', value: String(data?.clients || 0) },
        { icon: Receipt, label: 'Scontrino medio', value: euro(data?.ticket || 0) },
    ];

    const chartTooltip = {
        contentStyle: { borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 },
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-3xl"
            headerClassName="bg-white border-b border-gray-100"
            headerContent={
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pr-8">
                    <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-base font-bold uppercase shrink-0 ${hairdresser.avatarColor || 'bg-clip-100 text-clip-600'}`}>
                            {hairdresser.initials}
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 leading-none">{hairdresser.name}</h2>
                            <p className="text-sm text-gray-400 mt-1">Analisi performance</p>
                        </div>
                    </div>
                    <div className="flex flex-col items-stretch sm:items-end gap-2">
                        <div className="flex items-center gap-2">
                            <AnalyticsDateSelector value={customActive ? '' : period} onChange={(v: string) => { setPeriod(v); setCustomOpen(false); }} />
                            <button
                                type="button"
                                onClick={() => setCustomOpen((o) => !o)}
                                className={`px-3 h-[42px] rounded-xl border text-sm font-bold transition-colors shrink-0 ${customActive || customOpen ? 'bg-clip-50 border-clip-200 text-clip-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                            >
                                Da/A
                            </button>
                        </div>
                        {customOpen && (
                            <div className="flex items-center gap-2">
                                <input type="date" value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)} className="p-2 text-sm bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-clip-200" />
                                <span className="text-gray-400 text-sm">→</span>
                                <input type="date" value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)} className="p-2 text-sm bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-clip-200" />
                            </div>
                        )}
                    </div>
                </div>
            }
        >
            <div className="space-y-6">
                {/* KPI */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {kpis.map((c) => (
                        <div key={c.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                            <div className="flex items-center gap-1.5 text-clip-500 text-[11px] font-bold uppercase tracking-wider">
                                <c.icon size={13} /> {c.label}
                            </div>
                            <p className="text-xl font-bold text-gray-900 mt-1">{c.value}</p>
                        </div>
                    ))}
                </div>

                {/* Fatturato vs Costo */}
                <div className="rounded-2xl border border-gray-100 shadow-sm p-5 bg-white">
                    <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                            <TrendingUp size={18} className="text-clip-500" /> Fatturato vs costo
                        </h3>
                        {comp && ratio != null && (
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl font-bold text-sm ${over ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                {over ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                                {over ? 'Overperforming' : 'Underperforming'} · {ratio.toFixed(2)}×
                            </div>
                        )}
                    </div>
                    {comp ? (
                        <>
                            <div className="h-40 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={[{ name: 'Produzione', value: Math.round(data?.revenue || 0) }, { name: 'Costo', value: Math.round(costForPeriod) }]}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} tickFormatter={(v) => `€${v}`} />
                                        <Tooltip {...chartTooltip} formatter={(v: number) => [euro(v), '']} />
                                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                            <Cell fill={PRIMARY} />
                                            <Cell fill="#9CA3AF" />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                                <div className="rounded-xl bg-gray-50 py-2.5">
                                    <p className="text-[11px] font-bold text-gray-400 uppercase">Produzione</p>
                                    <p className="font-bold text-gray-900">{euro(data?.revenue || 0)}</p>
                                </div>
                                <div className="rounded-xl bg-gray-50 py-2.5">
                                    <p className="text-[11px] font-bold text-gray-400 uppercase">Costo</p>
                                    <p className="font-bold text-gray-900">{euro(costForPeriod)}</p>
                                </div>
                                <div className={`rounded-xl py-2.5 ${margin >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase">Margine</p>
                                    <p className={`font-bold ${margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>{euro(margin)}</p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">Imposta lo stipendio nella tab "Compensi & Performance" per confrontare la produzione con il costo.</p>
                    )}
                </div>

                {/* Servizi effettuati */}
                <div className="rounded-2xl border border-gray-100 shadow-sm p-5 bg-white">
                    <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 mb-4">
                        <Scissors size={18} className="text-clip-500" /> Servizi effettuati
                    </h3>
                    {data && data.services.length > 0 ? (
                        <div className="space-y-2.5">
                            {data.services.map((s) => {
                                const max = data.services[0].count || 1;
                                return (
                                    <div key={s.name} className="flex items-center gap-3">
                                        <span className="text-sm text-gray-700 font-medium w-40 truncate shrink-0">{s.name}</span>
                                        <div className="flex-1 h-6 bg-gray-100 rounded-lg overflow-hidden">
                                            <div className="h-full bg-clip-400 rounded-lg" style={{ width: `${Math.max(6, (s.count / max) * 100)}%` }} />
                                        </div>
                                        <span className="text-sm text-gray-500 w-24 text-right shrink-0">{s.count} · {euro(s.revenue)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-400">Nessun servizio nel periodo.</p>
                    )}
                </div>

                {/* Giorni + Orari */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-gray-100 shadow-sm p-5 bg-white">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 mb-4">
                            <CalendarDays size={18} className="text-clip-500" /> Giorni più attivi
                        </h3>
                        <div className="h-44 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data?.byWeekday || []}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} allowDecimals={false} />
                                    <Tooltip {...chartTooltip} formatter={(v: number) => [`${v} appunt.`, '']} />
                                    <Bar dataKey="value" fill={PRIMARY} radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="rounded-2xl border border-gray-100 shadow-sm p-5 bg-white">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 mb-4">
                            <Clock size={18} className="text-clip-500" /> Fasce orarie
                        </h3>
                        <div className="h-44 w-full">
                            {data && data.byHour.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={data.byHour}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip {...chartTooltip} formatter={(v: number) => [`${v} appunt.`, '']} labelFormatter={(l) => `Ore ${l}:00`} />
                                        <Bar dataKey="value" fill="#C05A40" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-sm text-gray-400">Nessun dato orario.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default StaffPerformanceDetailModal;
